import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatPrice, formatMoney, applyPriceTiers, isVideoUrl, isBundleProduct, getBundleTier, bundleOptions } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Heart, Truck, ShieldCheck, Minus, Plus, Package } from "lucide-react";
import ShareButtons from "../components/ShareButtons";

const ProductDetail = () => {
  const { slug } = useParams();
  const { t, lang, currency, addToCart } = useApp();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedBundle, setSelectedBundle] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setActiveImg(0);
      setQty(1);
      setSelectedVariant(r.data.variants?.[0] || null);
      setSelectedBundle(1);
    }).catch(() => {});
  }, [slug]);

  if (!product) return <div className="max-w-7xl mx-auto px-6 py-20 text-center">Loading…</div>;
  const name = product[`name_${lang}`] || product.name_en;
  const desc = product[`description_${lang}`] || product.description_en;
  const images = (product.images && product.images.length ? product.images : [""]).filter(Boolean);
  const variants = product.variants || [];
  const displayImg = (selectedVariant?.image) || images[activeImg] || images[0];
  const bundleMode = isBundleProduct(product);
  const options = bundleMode ? bundleOptions(product) : [];
  const tiers = (product.price_tiers || []).slice().sort((a, b) => a.min_qty - b.min_qty);
  const tiered = applyPriceTiers(product, qty);
  const tierActive = !bundleMode && tiered.tier !== null;
  const activeBundle = bundleMode ? getBundleTier(product, selectedBundle) || { min_qty: 1, price_idr: product.price_idr, price_usd: product.price_usd } : null;
  const maxPkgs = bundleMode ? Math.max(1, Math.floor((product.stock || 0) / (selectedBundle || 1))) : product.stock;
  const outOfStock = product.stock <= 0;
  // Show clean per-pc equivalent only for packs > 1 when it divides evenly (avoids ugly Rp133.333,33)
  const evenPerPc = bundleMode && activeBundle && selectedBundle > 1 && activeBundle.price_idr % selectedBundle === 0 ? activeBundle.price_idr / selectedBundle : null;

  const handleAdd = (qtyToAdd) => {
    const productWithVariant = selectedVariant ? { ...product, name_id: `${product.name_id} - ${selectedVariant.name}`, name_en: `${product.name_en} - ${selectedVariant.name}`, images: [displayImg] } : product;
    addToCart(productWithVariant, qtyToAdd, bundleMode ? selectedBundle : 0);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <Link to="/shop" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-holy-red">← {t("nav_shop")}</Link>
      <div className="grid md:grid-cols-2 gap-12 mt-6">
        <div>
          <div className="bg-ivory rounded-2xl overflow-hidden aspect-square flex items-center justify-center border border-border/50">
            {displayImg && (
              isVideoUrl(displayImg) ? (
                <video
                  src={displayImg}
                  controls
                  playsInline
                  preload="metadata"
                  onLoadedMetadata={(e) => { e.target.currentTime = 0.1; }}
                  className="w-full h-full object-contain"
                  data-testid="product-video"
                />
              ) : (
                <img src={displayImg} alt={name} className="w-full h-full object-contain" data-testid="product-image" />
              )
            )}
          </div>
          {images.length > 1 && !selectedVariant?.image && (
            <div className="grid grid-cols-5 gap-2 mt-3" data-testid="product-gallery">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`gallery-thumb-${i}`}
                  className={`aspect-square bg-ivory rounded-lg overflow-hidden border-2 transition-all ${i === activeImg ? "border-holy-red" : "border-transparent hover:border-border"}`}
                >
                  {isVideoUrl(img) ? (
                    <video
                      src={img}
                      muted
                      playsInline
                      preload="metadata"
                      onLoadedMetadata={(e) => { e.target.currentTime = 0.1; }}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-holy-gold-dark font-semibold mb-2">
            {product.category}
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl text-foreground mb-3" data-testid="product-name">{name}</h1>
          <div className="mb-6">
            {bundleMode && activeBundle ? (
              <>
                <div className="flex items-baseline gap-3" data-testid="product-price">
                  <span className="text-2xl text-holy-red font-semibold">
                    {formatMoney(activeBundle.price_idr, activeBundle.price_usd, currency)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {selectedBundle === 1 ? "/pc" : `/${lang === "id" ? "paket" : "pack"} ${selectedBundle} pcs`}
                  </span>
                </div>
                {evenPerPc && (
                  <div className="text-xs text-muted-foreground mt-1" data-testid="bundle-perpc">
                    ≈ {formatMoney(evenPerPc, activeBundle.price_usd / selectedBundle, currency)} /pc
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex items-baseline gap-3" data-testid="product-price">
                  <span className="text-2xl text-holy-red font-semibold">
                    {formatMoney(tiered.price_idr, tiered.price_usd, currency)}
                  </span>
                  {tierActive && (
                    <span className="text-sm text-muted-foreground line-through">{formatPrice(product, currency)}</span>
                  )}
                </div>
                {tierActive && (
                  <div className="text-xs text-holy-green font-semibold mt-1" data-testid="tier-savings">
                    {lang === "id" ? `Hemat! Harga grosir untuk ${tiered.tier.min_qty}+ pcs` : `Wholesale price for ${tiered.tier.min_qty}+ pcs`}
                  </div>
                )}
              </>
            )}
          </div>

          {bundleMode ? (
            <div className="mb-6 bg-cream rounded-xl border border-border/50 p-4" data-testid="bundle-selector">
              <div className="text-[10px] uppercase tracking-widest text-holy-gold-dark font-semibold mb-2 flex items-center gap-1.5">
                <Package className="w-3.5 h-3.5" /> {lang === "id" ? "Pilih Satuan / Paket" : "Choose Single / Pack"}
              </div>
              <div className="flex flex-wrap gap-2">
                {options.map((b) => {
                  const sel = b.min_qty === selectedBundle;
                  return (
                    <button
                      key={b.min_qty}
                      onClick={() => { setSelectedBundle(b.min_qty); setQty(1); }}
                      data-testid={`bundle-option-${b.min_qty}`}
                      className={`px-4 py-2.5 rounded-xl border text-center transition-all ${
                        sel ? "border-holy-red bg-white ring-2 ring-holy-red/20 text-holy-red" : "border-border bg-white hover:border-holy-red/50"
                      }`}
                    >
                      <span className="block text-sm font-semibold">{b.min_qty === 1 ? "1 pc" : `${b.min_qty} pcs`}</span>
                      <span className="block text-xs mt-0.5">{formatMoney(b.price_idr, b.price_usd, currency)}</span>
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-muted-foreground mt-3" data-testid="bundle-moq">
                {lang === "id"
                  ? "Bisa beli satuan (1 pc) atau per paket untuk harga lebih hemat."
                  : "Buy single (1 pc) or in packs for better value."}
              </div>
            </div>
          ) : tiers.length > 0 && (
            <div className="mb-6 bg-cream rounded-xl border border-border/50 p-4" data-testid="wholesale-tiers">
              <div className="text-[10px] uppercase tracking-widest text-holy-gold-dark font-semibold mb-2">
                {lang === "id" ? "Harga Grosir" : "Wholesale Pricing"}
              </div>
              <div className="space-y-1.5">
                <div className={`flex justify-between text-sm rounded-lg px-3 py-1.5 ${!tierActive ? "bg-white ring-1 ring-holy-red/30" : ""}`}>
                  <span>{lang === "id" ? "Beli 1" : "Buy 1"}+ pcs</span>
                  <span className="font-semibold">{formatPrice(product, currency)} /pc</span>
                </div>
                {tiers.map((t2) => (
                  <div
                    key={t2.min_qty}
                    data-testid={`tier-row-${t2.min_qty}`}
                    className={`flex justify-between text-sm rounded-lg px-3 py-1.5 ${tiered.tier?.min_qty === t2.min_qty ? "bg-white ring-1 ring-holy-red/30" : ""}`}
                  >
                    <span>{lang === "id" ? "Beli" : "Buy"} {t2.min_qty}+ pcs</span>
                    <span className="font-semibold text-holy-red">{formatMoney(t2.price_idr, t2.price_usd, currency)} /pc</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-muted-foreground leading-relaxed mb-6">{desc}</p>

          {variants.length > 0 && (
            <div className="mb-6" data-testid="variant-selector">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                {lang === "id" ? "Pilih Warna" : "Choose Color"}: <span className="text-foreground font-semibold normal-case">{selectedVariant?.name}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {variants.map((v, i) => {
                  const sel = selectedVariant?.name === v.name;
                  return (
                    <button
                      key={i}
                      onClick={() => setSelectedVariant(v)}
                      data-testid={`variant-${v.name}`}
                      title={v.name}
                      className={`w-10 h-10 rounded-full border-2 transition-all relative ${sel ? "border-holy-red ring-4 ring-holy-red/20 scale-110" : "border-border hover:border-holy-red/50"}`}
                      style={{ backgroundColor: v.color_hex || "#fff" }}
                    >
                      {v.image && !v.color_hex && <img src={v.image} alt={v.name} className="w-full h-full rounded-full object-cover" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mb-6">
            {bundleMode && selectedBundle > 1 && (
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{lang === "id" ? "Jumlah Paket" : "Packs"}</span>
            )}
            <div className="flex items-center border border-border rounded-full overflow-hidden">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2 hover:bg-cream" data-testid="qty-minus"><Minus className="w-3.5 h-3.5" /></button>
              <span className="px-4 text-sm font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(maxPkgs, q + 1))} className="px-3 py-2 hover:bg-cream" data-testid="qty-plus"><Plus className="w-3.5 h-3.5" /></button>
            </div>
            <span className="text-xs text-muted-foreground">{product.stock} {t("stock")}</span>
          </div>

          <div className="flex gap-3">
            <button
              data-testid="add-to-cart-btn"
              onClick={() => handleAdd(qty)}
              className="flex-1 bg-holy-red hover:bg-holy-red-dark text-ivory rounded-full px-6 py-3.5 transition-all hover:-translate-y-0.5 hover:shadow-lg"
              disabled={outOfStock}
            >
              {t("add_to_cart")}
            </button>
            <button
              data-testid="buy-now-btn"
              onClick={() => { handleAdd(qty); navigate("/checkout"); }}
              className="flex-1 border border-holy-red text-holy-red hover:bg-holy-red/5 rounded-full px-6 py-3.5 transition-all"
              disabled={outOfStock}
            >
              {t("buy_now")}
            </button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-2"><Heart className="w-4 h-4 text-holy-red" /> {t("handmade_with_love")}</div>
            <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-holy-green" /> JNE/J&T/Pos</div>
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-holy-gold-dark" /> {t("secure_payment")}</div>
          </div>

          <ShareButtons product={product} />
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
